import logger from "utils/logger";
import { getProp, qs, qsa } from "utils/utils";
import since from "utils/time-since";
import tgamConfig from "../../config/tgam";
import { isRegistered, isSubscribed } from "lib/identity/api";
import { validStatus, handleJSON } from "utils/fetch";
import { local } from "lib/store";

const debugTag = "[Your Globe Overlay]";

// Classes
let overlay;
let overlayTrigger;
let overlayCloseButtons = [];
let scrollMenuLeftMask;
let scrollMenuRightMask;

const hiddenClass = "c-your-globe--hidden";
const hidingClass = "c-your-globe--hiding";
const revealingClass = "c-your-globe--revealing";
const noScrollClass = "u-no-scroll--not-fixed";
const siteHeader = qs(".c-site-header");
const scrollMenuContainer = qs(".c-site-header__bottom-content");
const scrollMenuList = qs(".c-site-header__bottom .c-scroll-menu__list");
const overlayTriggerDotClasses = [
  "c-your-globe__trigger--dot--unread",
  "c-your-globe__trigger--dot--no-follow"
];

const keyLocalStorage = "tgam.globeOverlayDate";

// State (for throttled requestAnimationFrame)
let waiting = false;

// ************************************************
// Helper functions
// ************************************************

// Get the environment
function getEnv() {
  const env = tgamConfig.env.isProdOrStage === true ? "prd" : "dev";
  return env;
}

// ************************************************
// Functions to construct the story cards
// ************************************************

/**
 * Get the label data if exists
 * @param {Object} article - that contains label
 * @returns {Object} of label
 */
function getLabel(article) {
  let labelText = "";
  const secLabel = getProp(article, "section_label", "").toLowerCase();
  const articleDisplay = getProp(article, "label.basic.display", false);
  const articleText = getProp(article, "label.basic.text", "").toLowerCase();

  if (secLabel) {
    labelText = secLabel;
  }

  if (articleDisplay && articleText && !labelText) {
    labelText = articleText;
  }

  return labelText;
}

/**
 * Get all the data to display an article's photo
 * @param {Object} article - that contains image
 * @param {String} label - story label
 * @returns {Object} of image
 */
function getImage(article, label) {
  let path;
  let alt;
  const isOpinion = label.text === "opinion" || article.subtype === "column";
  const authors = getProp(article, "authors", []);
  const authorByline = getProp(article, "authors[0].byline", null);
  const authorImage = getProp(article, "authors[0].metadata.image", null);
  const articleCreditImage = getProp(article, "credits.by[0].image.url", null);
  const articleCreditName = getProp(article, "credits.by[0].name", null);
  const articlePromoUrl = getProp(article, "promo_items.basic.url", null);
  const articlePromoCaption = getProp(article, "promo_items.basic.caption", null);
  const articlePictureRelUrl = getProp(article, "pictureRel[0].url200", null);
  const articlePictureRelCaption = getProp(article, "pictureRel[0].caption", null);

  // Show author image if opinion/column article
  if (isOpinion) {
    path = authors.length ? authorImage : articleCreditImage;
    alt = authors.length ? authorByline : articleCreditName;
    return { path, alt };
  }

  // Article image
  path = articlePictureRelUrl !== null ? articlePictureRelUrl : articlePromoUrl;
  alt = articlePromoCaption !== null ? articlePromoCaption : articlePictureRelCaption;

  return { path, alt };
}

/**
 * Generate a template for the time. Based on PB dateDisplay.tag
 * @param {Object} article - to process
 * @returns {String}
 */
function displayDateTag(article) {
  const storyPublishedDate = storyPublishedDateTag(article);
  let storyPubDateFormatted = new Date(storyPublishedDate);
  storyPubDateFormatted = Math.round(storyPubDateFormatted.getTime() / 1000);

  if (!storyPublishedDate || !window.Intl || !window.Intl.DateTimeFormat) {
    return "";
  }

  const storyDate = storyTimeDisplayTag({
    pubdate: storyPublishedDate,
    update: article.last_updated_date
  });

  const { published } = storyDate;
  const hideUpdate = storyDate.field === "published";
  const publishedClassNames =
    storyDate.moment === "published" || storyDate.moment === "both" ? " jx-story-moment" : "";
  let updatedClassNames =
    (storyDate.moment === "updated" || storyDate.moment === "both" ? " jx-story-moment" : "") +
    (hideUpdate ? " u-visually-hidden" : "");
  updatedClassNames = `${updatedClassNames} c-timestamp--updated`;

  // "Time ago" timestamp
  let date = since(storyPubDateFormatted);
  if (date === "") {
    // If "time ago" is empty (means article is older than three days) display the date
    date = published;
  }

  return `
    <time class="c-card__meta-time u-no-wrap ${publishedClassNames}">${date}</time>
    <time class="c-card__meta-time u-no-wrap ${updatedClassNames}">Updated</time>
  `;
}

// Based on PB storyTimeDisplay.tag
function storyPublishedDateTag(story) {
  return story.display_date || story.publish_date;
}

// Based on PB storyTimeDisplay.tag
function storyTimeDisplayTag(params) {
  const isSectionPageContext = true;
  const pubDate = new Date(params.pubdate);
  const upDate = new Date(params.update);
  const now = new Date();

  const oneYear = 31536000;
  const oneDay = 86400;
  const threeDays = 259200;
  const tenMinutes = 600;
  const unixNow = Math.round(now.getTime() / 1000);
  const unixPubdate = Math.round(pubDate.getTime() / 1000);
  const unixUpdate = Math.round(upDate.getTime() / 1000);
  const timeSincePublication = unixNow - unixPubdate;
  const timeSinceUpdate = unixNow - unixUpdate;

  let pattern = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "America/Toronto"
  };

  if (params.format) {
    pattern = params.format;
  } else if (isSectionPageContext && timeSincePublication < oneYear) {
    pattern = {
      month: "long",
      day: "numeric",
      timeZone: "America/Toronto"
    };
  }

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
  const formatter = new Intl.DateTimeFormat("en-US", pattern);

  const formattedPubDate = formatter.format(pubDate);
  const formattedUpDate = formatter.format(upDate);

  const publishedAndUpdatedAreTheSameDay = formattedPubDate === formattedUpDate;
  const publishedLessThanThreeDaysAgo = timeSincePublication < threeDays;
  const updatedWasLessThanThreeDaysAgo = timeSinceUpdate < threeDays;
  const storyWasUnderEmbargoOrSimilar = unixPubdate > unixUpdate;
  const storyHasBeenUpdatedMoreThanTenMinutesSincePublication =
    !storyWasUnderEmbargoOrSimilar && unixUpdate - unixPubdate > tenMinutes;
  const showPublishedDateAsMoment = publishedLessThanThreeDaysAgo;
  const showUpdatedDateAsMoment =
    !isSectionPageContext &&
    storyHasBeenUpdatedMoreThanTenMinutesSincePublication &&
    updatedWasLessThanThreeDaysAgo;

  let field = "published"; // Which fields to show (published, updated, or both)
  let moment = null; // Which fields to show in relative date (i.e., moment) format

  if (storyHasBeenUpdatedMoreThanTenMinutesSincePublication) {
    if (publishedAndUpdatedAreTheSameDay && timeSinceUpdate < oneDay) {
      field = "both"; // Don't show updated date since it's the same as published date.
    }
  }

  if (showUpdatedDateAsMoment) {
    moment = "updated";

    if (showPublishedDateAsMoment) {
      moment = "both";
    }
  } else if (showPublishedDateAsMoment) {
    moment = "published";
  }

  return {
    published: formattedPubDate,
    updated: formattedUpDate,
    field: field,
    moment: moment
  };
}

// ************************************************
// Following functionality
// ************************************************

/**
 * Attach event listeners to following icons/buttons
 */
function addFollowingFunctionality() {
  const btns = qsa(".c-your-globe__overlay .c-topic-button");
  if (!btns.length) {
    return;
  }
  btns.forEach(function fn(btn) {
    btn.addEventListener("mouseover", function fn() {
      btn.classList.remove("js-topic-clicked");
      buttonToggle(btn, "over");
    });

    btn.addEventListener("mouseout", function fn() {
      buttonToggle(btn, "out");
    });

    btn.addEventListener("click", function fn() {
      updateFollowUnfollowStatus(btn);
      btn.classList.add("js-topic-clicked");
    });
  });
}

/**
 * Follow or unfollow a topic
 * @param {HTMLElement} el - button element
 */
function updateFollowUnfollowStatus(el) {
  const status = el.getAttribute("data-status");
  const topicSlug = el.getAttribute("data-slug");
  const topicName = el.getAttribute("data-topic");
  const isFollowingAuthor = el.dataset.topicType === "author";
  const topicType = isFollowingAuthor ? "author" : "topic";
  const action = status === "following" ? "unfollow" : "follow";

  followUnfollowTopic(action, topicSlug, isFollowingAuthor).then(function fn(res) {
    logger.info(`${debugTag} fetch success: `, res);
    const setStatus = "following";
    const text = "following";
    const ariaLabelText = "Unfollow this " + topicType + ": " + topicName;
    if (status === "following") {
      setStatus = "unfollowing";
      text = "Follow";
      ariaLabelText = "Follow this " + topicType + ": " + topicName;
    }
    analyticsLayer.push({
      event: "clickEvent",
      clickEvent: {
        type: "button",
        feature: isFollowingAuthor ? "author" : "topic",
        label: topicName.toLowerCase(),
        action: action
      }
    });

    el.dataset.status = setStatus;
    el.setAttribute("aria-label", ariaLabelText);
    qs(".c-button__text", el).innerText = text;
  });
}

/**
 * Update icon state on mouseover/mouseout
 * @param {HTMLElement} el - button element
 * @param {String} action - either "out" or "over"
 */
function buttonToggle(el, action) {
  const status = el.getAttribute("data-status");
  const text = "Follow";
  const hoverText = "Follow";
  if (status === "following") {
    text = "Following";
    hoverText = "Unfollow";
  }

  if (action === "out" && !el.classList.contains("js-topic-clicked")) {
    qs(".c-button__text", el).innerText = text;
  } else if (
    action === "out" &&
    el.classList.contains("js-topic-clicked") &&
    status === "unfollowing"
  ) {
    qs(".c-button__text", el).innerText = "Follow";
  } else if (action === "over") {
    qs(".c-button__text", el).innerText = hoverText;
  }
}

// ************************************************
// Helper functions to build the markup
// ************************************************

/**
 * Generate latest stories after some filtering
 * @param {Array} latest - array of days
 * @returns {Array} of filtered articles
 */
function generateLatestStories(latest) {
  // Latest is an array of days, so we need to create a new array of stories from last three days
  const articles = latest.reduce(function fn(result, item) {
    return result.concat(item.items);
  }, []);
  // We need to display only five latest articles to the user
  return articles.slice(0, 5);
}

/**
 * For every topic - generate one unique story
 * @param {Array} topics - array of topics (with five stories for every topic)
 * @returns {Array} - array of topics with one unique story
 */
function generateUniqueStory(topics) {
  const uniqueArticles = topics.map(function fn(t) {
    t.items = t.items.slice(0, 1); // Only one story is needed
    return t;
  });
  return uniqueArticles;
}

/**
 * Generates image template
 * @param {Object} image - to render
 * @returns {String}
 */
function displayImage(image) {
  if (!image.path) {
    return "";
  }

  return `
    <div class="c-image-container c-image-container--lazy" style="padding-bottom: 66.66666666666666%">
      <div class="c-image-container--lazy__wrapper">
        <img class="c-image" loading="lazy" src="${image.path}" alt="${image.alt}">
      </div>
    </div>
  `;
}

/**
 * Generate a template for the label
 * @param {String} label - the label text
 * @returns {String}
 */
function displayLabel(label) {
  if (label !== "opinion") {
    return "";
  }

  return `
    <div class="c-card__meta">
      <span class='c-card__label c-card__label--muted'>${label}</span>
    </div>
  `;
}

/**
 * Display "Follow", "Following" buttons
 * @param {Object} tData
 * @returns {String}
 */
function displayFollowingButton(tData) {
  const { topicName, topicSlug, topicType, topicImg } = tData;
  const isAuthor = topicType === "author";
  const authorImg =
    isAuthor && topicImg
      ? `<img class="c-topic-author-photo" src="${topicImg}" alt="${topicName}">`
      : "";
  const template = isAuthor ? "/authors/" : "/topics/";
  const href = window.tgam.env.baseRootAbsoluteUrl + template + topicSlug;
  const linkClasses = isAuthor ? "c-topic-link c-topic-link--author" : "c-topic-link";

  return `
    <div class="c-topic-group__body">
      <div class="c-topic-group__items">
        <div class="c-topic">
          <a href="${href}" class="${linkClasses}"> ${authorImg} ${topicName}</a>
          <button class="c-button c-button--white-primary c-button--small c-topic-button" data-status="unfollowing" type="button" aria-label="Follow this topic: ${topicName}" data-slug="${topicSlug}" data-topic-type="${topicType}" data-topic="${topicName}">
            <span class="c-button__text"> Follow </span>
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Generates story card markup
 * @param {Object} article - to display
 * @param {Object} topicData - (topicName, topicSlug, topicType, topicVariation, topicImg)
 * @param {Boolean} addFollowButton
 * @returns {String} story card markup
 */
function storyCard(article, topicData, addFollowButton) {
  if (!article || !topicData) {
    return "";
  }
  const { topicName, topicType, topicVariation } = topicData;
  let followingTopic;
  let timeTemplate;
  const analyticsModifier = `${topicType}: `;

  if (addFollowButton) {
    // Display a follow button beside the topic name
    followingTopic = displayFollowingButton(topicData);
    timeTemplate = displayDateTag(article);
  }
  const label = getLabel(article);
  const image = getImage(article, label);
  const labelTemplate = displayLabel(label);
  const imageTemplate = displayImage(image);
  const headline = article.headlines.basic;
  const href = window.tgam.env.baseUrl + article.canonical_url;
  const contentId = article._id;

  const dataAnalyticsClick = JSON.stringify({
    type: "link",
    feature: "following feed",
    contentId: contentId,
    label: `${analyticsModifier}${topicName.toLowerCase()}: ${headline.toLowerCase()}`,
    page: `sec:homepage:personalized feed:${topicVariation}`,
    hierarchy: 1
  });

  let cardMarkup = `
    <div class="c-card__topics">
      ${followingTopic}
    </div>
    <a class="c-card__grid c-card__link"
      data-sophi="${contentId}"
      data-analyticsclick='${dataAnalyticsClick}'
      href="${href}">
      <div class="c-card__content">
        <div class="c-card__hed">
          <span class="c-card__hed-text">${headline}</span>
        </div>
        <span class="c-card__meta">${timeTemplate}</span>
      </div>
      <div class='c-card__media'>${imageTemplate}</div>
    </a>
  `;

  if (!followingTopic) {
    cardMarkup = `
      <a class="c-card__grid c-card__link"
        data-sophi="${contentId}"
        data-analyticsclick='${dataAnalyticsClick}'
        href="${href}">
        <div class="c-card__content">
          <div class="c-card__topics">
            <span class="c-card__topics__item">${topicName}</span>
          </div>
          <div class="c-card__hed">
            <span class="c-card__hed-text">${headline}</span>
          </div>
          ${labelTemplate}
        </div>
        <div class="c-card__media">${imageTemplate}</div>
      </a>
    `;
  }

  return `
    <div class="c-card c-card--following c-card--latest">
      ${cardMarkup}
    </div>
  `;
}

/**
 * Adds the overlay trigger dot class
 * @param {String} type - "unread" or "no-follow"
 */
function addOverlayTriggerDot(type) {
  overlayTriggerDotClasses.forEach(function fn(dotClass) {
    if (dotClass === "c-your-globe__trigger--dot--" + type) {
      overlayTrigger.classList.add(dotClass);
    } else {
      overlayTrigger.classList.remove(dotClass);
    }
  });
  overlayTrigger.classList.add("c-your-globe__trigger--dot");
}

/**
 * Removes the overlay trigger dot class
 */
function removeOverlayTriggerDot() {
  overlayTriggerDotClasses.forEach(function fn(dotClass) {
    overlayTrigger.classList.remove(dotClass);
  });
  overlayTrigger.classList.remove("c-your-globe__trigger--dot");
}

/**
 * Returns a heading element to be displayed inside the overlay
 * @param {String} text
 * @returns {String}
 */
function overlayLabel(text) {
  return `<h3 class="c-your-globe__overlay-label">${text}</h3>`;
}

const overlayHeadingHasFollowed = `
    ${overlayLabel("The latest in topics and authors you follow")}
    <p class="c-your-globe__overlay-text">View more in <a href="/your-globe/#following" class="c-your-globe__overlay-link">Following</a></p>
  `;

const overlayHeadingNoFollowed = `
  ${overlayLabel("Get started: build your personal news feed")}
  <ol class="c-your-globe__overlay-list">
    <li>Follow topics relevant to your reading interests.</li>
    <li>Check back here or <a href='/your-globe/#following' class='c-your-globe__overlay-link'>your Following page</a> to view the latest articles on your topics.</li>
  </ol>
  `;

const upToDateMessage =
  "<p class='c-your-globe__overlay-text'>You’re up to date on your Following feed. Check again later for new stories.</p>";

// ************************************************
// Parse API response and inject markup into overlay
// ************************************************

// Story card markup

/**
 * Stories originally came from the following locations in the API response:
 * - data.articles[i].items[i].topics
 * - data.articles[i].items[i].authors
 * @param {Array} stories
 * @returns {String} story card markup
 */
function latestStoryCards(stories) {
  logger.info(`${debugTag} Display latest stories based on these stories: `, stories);

  const storyCards = stories
    .map(function fn(story) {
      // The "topics" and "authors" arrays will only contain one item
      // (i.e. the topic or author that the user is following)
      let topic;
      let topicData;

      if (story.topics && story.topics.length) {
        // Normal topic
        topic = story.topics[0];
        topicData = {
          topicName: topic.name,
          topicSlug: topic.slug,
          topicType: "topic",
          topicVariation: "following"
        };
      } else if (story.authors && story.authors.length) {
        // Author topic
        topic = story.authors[0];
        // Only authors have images associated with them - normal topics do not
        const authorImg = topic.metadata && topic.metadata.image ? topic.metadata.image : null;
        topicData = {
          topicName: topic.byline,
          topicSlug: topic.slug,
          topicType: "author",
          topicVariation: "following",
          topicImg: authorImg
        };
      }
      // Don't display a follow button beside the topic because the user is already following it
      return storyCard(story, topicData, false);
    })
    .join("");

  return storyCards;
}

/**
 * @param {Array} topics
 * @param {String} variation - "recommended" or "trending" (used for the click tracking analytics)
 * @returns {String} story card markup
 */
function recommendedTrendingStoryCards(topics, variation) {
  const uniqueStories = generateUniqueStory(topics);
  const storyCards = uniqueStories
    .map(function fn(topic) {
      // Grab one article
      const story = topic.items[0];
      // Only authors have images associated with them - normal topics do not
      const authorImg =
        topic.authorTopic && topic.authorMetadata && topic.authorMetadata.image
          ? topic.authorMetadata.image
          : null;
      // Normally we'd display all of the topics that are assigned to an article, but
      // this API groups articles by topic, so we only have access to that one topic.
      // Even if we could display additional topics, we wouldn't want to becuase having
      // multiple follow buttons would junk up the UI.
      const topicData = {
        topicName: topic.name,
        topicSlug: topic.slug,
        topicType: topic.authorTopic ? "author" : "topic",
        topicVariation: variation,
        topicImg: authorImg
      };
      // Display the follow button beside the topic because we're suggesting new topics to follow
      return storyCard(story, topicData, true);
    })
    .join("");

  return storyCards;
}

/**
 * Topics originally came from the following locations in the API response:
 * - data.recommendedAuthors
 * - data.recommendedTopics
 * @param {Array} topics
 * @returns {String} story card markup
 */
function recommendedStoryCards(topics) {
  logger.info(`${debugTag} Display recommended stories based on these topics:`, topics);
  return recommendedTrendingStoryCards(topics, "recommended");
}

/**
 * Topics originally came from the following location in the API response:
 * - data.trendingTopics
 * @param {Array} topics
 * @returns {String} story card markup
 */
function trendingStoryCards(topics) {
  logger.info(`${debugTag} Display trending stories based on these topics:`, topics);
  return recommendedTrendingStoryCards(topics, "trending");
}

// Markup inside the overlay

/**
 * @param {Array} stories
 * @returns {Object} markup for the overlay's header and body content areas
 */
function showLatestStories(stories) {
  logger.info(`${debugTag} Scenario: latest stories`);
  const storyCardsMarkup = latestStoryCards(stories);
  return {
    header: overlayHeadingHasFollowed,
    body: storyCardsMarkup
  };
}

/**
 * @param {Array} topics
 * @returns {Object} markup for the overlay's header and body content areas
 */
function upToDateShowRecommended(topics) {
  logger.info(`${debugTag} Scenario: up to date, show recommended`);
  const storyCardsMarkup = recommendedStoryCards(topics);
  return {
    header: overlayHeadingHasFollowed,
    body: upToDateMessage + overlayLabel("Recommended for you") + storyCardsMarkup
  };
}

/**
 * @param {Array} topics
 * @returns {Object} markup for the overlay's header and body content areas
 */
function upToDateShowTrending(topics) {
  logger.info(`${debugTag} Scenario: up to date, show trending`);
  const storyCardsMarkup = trendingStoryCards(topics);
  return {
    header: overlayHeadingHasFollowed,
    body: upToDateMessage + overlayLabel("Trending topics to follow") + storyCardsMarkup
  };
}

/**
 * @param {Array} topics
 * @returns {Object} markup for the overlay's header and body content areas
 */
function notFollowingShowRecommended(topics) {
  logger.info(`${debugTag} Scenario: not following, show recommended`);
  const storyCardsMarkup = recommendedStoryCards(topics);
  return {
    header: overlayHeadingNoFollowed,
    body: overlayLabel("Recommended for you") + storyCardsMarkup
  };
}

/**
 * @param {Array} topics
 * @returns {Object} markup for the overlay's header and body content areas
 */
function notFollowingShowTrending(topics) {
  logger.info(`${debugTag} Scenario: not following, show trending`);
  const storyCardsMarkup = trendingStoryCards(topics);
  return {
    header: overlayHeadingNoFollowed,
    body: overlayLabel("Trending topics to follow") + storyCardsMarkup
  };
}

/**
 * Parse data from the personalized API and inject markup into the overlay
 * @see https://confluence.theglobeandmail.com/display/ARC/Logic+for+Embedded+on+Homepage++and+Your+Globe+Overlay
 * @param {Object} data - topic and story data provided by the API
 * @param {String} hashId - user's hash id
 */
function parsePerzonalizedTopicsData(data, hashId) {
  logger.info(`${debugTag} parsePerzonalizedTopicsData`, data);

  const totalTopicsFollowed = data.totalTopicsFollowed || 0;
  const totalAuthorsFollowed = data.totalAuthorsFollowed || 0;
  const recommendedTopics = data.recommendedTopics || [];
  const recommendedAuthors = data.recommendedAuthors || [];
  const latestStories = data.articles || [];
  let topics = [];
  let stories = [];
  let markup = "";

  if (totalTopicsFollowed || totalAuthorsFollowed) {
    if (latestStories.length) {
      // Scenario 1: "Latest stories"

      // Count the number of stories returned (for logging/debugging purposes only)
      const latestStoriesCount = latestStories.reduce(function _reduceStories(accumulator, date) {
        const urls = date.items.map(function _mapStories(article) {
          return article.canonical_url;
        });
        return accumulator.concat(urls);
      }, []).length;
      logger.info(`${debugTag} latestStoryUrls (${latestStoriesCount})`);

      // Fetch the user's reading history
      // TODO: this API request will no longer be necessary when Data Science makes
      // the "isRead" property functional (it's currently "false" 100% of the time)
      getReadingHistory(hashId).then(function _historyFetched(historyData) {
        const readingHistoryUrls = historyData.map(function _mapHistory(item) {
          return item.url;
        });
        logger.info(`${debugTag} readingHistoryUrls (${readingHistoryUrls.length})`);
        const currentArticleUrl =
          window.tgam.meta.pagetype === "story" ? window.tgam.meta.urlRelative : null;

        // Filter out stories that exist in the user's reading history
        const filteredStories = latestStories.map(function _mapDates(dateObj) {
          const items = dateObj.items.filter(function _filterStories(article) {
            const articleUrl = article.canonical_url;
            const shouldRemove =
              readingHistoryUrls.includes(articleUrl) ||
              article.isRead ||
              articleUrl === currentArticleUrl;
            if (shouldRemove) {
              if (articleUrl === currentArticleUrl) {
                logger.info(
                  `${debugTag} Currently reading this article, so removing: ` + articleUrl
                );
              } else {
                logger.info(`${debugTag} Has already been read, so removing: ` + articleUrl);
              }
            }
            return !shouldRemove;
          });
          return {
            date: dateObj.date,
            items: items
          };
        }, []);

        stories = generateLatestStories(filteredStories);
        markup = showLatestStories(stories);

        const atLeastOneStoryIsNew = checkNewStories(stories);
        if (atLeastOneStoryIsNew) {
          // Display indicator only when there is a new story since the last time the user checked the feed
          addOverlayTriggerDot("unread");
        }
        populateOverlay(markup, true);
      });
    } else {
      if (recommendedTopics.length || recommendedAuthors.length) {
        // Scenario 2: "Up to date, show recommended"
        topics = recommendedAuthors.concat(recommendedTopics);
        markup = upToDateShowRecommended(topics);
      } else {
        // Scenario 3: "Up to date, show trending"
        topics = data.trendingTopics || [];
        markup = upToDateShowTrending(topics);
      }
      populateOverlay(markup, true);
    }
  } else {
    topics = recommendedAuthors.concat(recommendedTopics);
    if (topics.length) {
      // Scenario 4: "Not following, show recommended"
      markup = notFollowingShowRecommended(topics);
    } else {
      // Scenario 5: "Not following, show trending"
      topics = data.trendingTopics || [];
      markup = notFollowingShowTrending(topics);
    }
    addOverlayTriggerDot("no-follow");
    populateOverlay(markup, false);
  }
}

/**
 * Populate the overlay with content and activate the "following" functionality
 * @param {String} markup - HTML markup
 * @param {Boolean} headerBorder - whether to include a header border
 */
function populateOverlay(markup, headerBorder) {
  const overlayHeader = qs(".c-your-globe__overlay-header");
  const overlayHeaderText = qs(".c-your-globe__overlay-header-text");
  const overlayBody = qs(".c-your-globe__overlay-body");

  if (!overlayHeader || !overlayHeaderText || !overlayBody) {
    return;
  }

  const spinner = qs(".c-spinner");
  spinner && spinner.parentElement.removeChild(spinner);

  overlayHeaderText.insertAdjacentHTML("afterbegin", markup.header);
  overlayBody.insertAdjacentHTML("afterbegin", markup.body);

  if (!headerBorder) {
    overlayHeader.classList.add("c-your-globe__overlay-header--no-border");
  }
  addFollowingFunctionality();
}

// ************************************************
// API calls
// ************************************************

/**
 * Fetch the user's reading history for the past seven days
 * @param {String} hashId - user's hash id
 * @returns {Promise}
 */
function getReadingHistory(hashId) {
  if (!hashId) {
    return Promise.reject("Unable to fetch reading history for: " + hashId);
  }
  const url =
    "https://tgamuserprofile.theglobeandmail.ca/upcj/readinghistory?days=7&limit=10&hashId=" +
    hashId;
  return fetch(url)
    .then(validStatus)
    .then(handleJSON)
    .then(function fn(data) {
      if (!data || !data.readingHistoryArticles) {
        const e = new Error("No reading history found for: " + url);
        return Promise.reject(e);
      }
      return data.readingHistoryArticles;
    })
    .catch(function efn(err) {
      logger.error(`${debugTag} fetch error for reading history: ${err}`);
    });
}

/**
 * Get the personalized feed
 * @param {String} hashId - user's hash id
 * @param {String} env - environment
 * @see https://confluence.theglobeandmail.com/display/ARC/Logic+for+Embedded+on+Homepage++and+Your+Globe+Overlay
 * @returns {Promise<Object>}
 */
function getPerzonalizedTopics(hashId, env) {
  const url = `https://myglobe-${env}.theglobeandmail.com/v1/user/personalizedarticles`;
  const postBody = {
    hashId: hashId,
    nonFollowerRecoTopicLimit: 2,
    hideRecoTopicsForHasLatest: true,
    showRecoArticlesForNoLatest: true
  };
  const config = {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(postBody)
  };

  return fetch(url, config)
    .then(function fn(res) {
      if (res.status !== 200) {
        throw `${debugTag} fetch error for personalized endpoint ${url}`;
      }
      return res.json();
    })
    .catch(function efn(err) {
      logger.error(`${debugTag} fetch error for personalized endpoint: ${err}`);
    });
}

/**
 * Follow or unfollow the topic
 * @param {String} action - "follow" or "unfollow"
 * @param {String} topic - topic name
 * @param {Boolean} isFollowingAuthor - true is following author button
 * @returns {Promise<Object>}
 */
function followUnfollowTopic(action, topic, isFollowingAuthor) {
  const path = isFollowingAuthor ? "author/" : "topic/";
  const env = getEnv();
  const hashId = getProp(tgamConfig, "datalayer.identity.guid", null);
  const url = "https://myglobe-" + env + ".theglobeandmail.com/v1/user/" + path + action;
  const postBody = {
    hashId: hashId,
    topicSlug: topic
  };
  const config = {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(postBody)
  };

  return fetch(url, config)
    .then(function fn(res) {
      if (res.status !== 200) {
        throw "Error following or unfollowing the topic: " + url;
      }
      return res.json();
    })
    .catch(function efn(err) {
      logger.error(`${debugTag} fetch error for follow/unfollow endpoint: ` + err);
    });
}

// ************************************************
// Overlay UI
// ************************************************

function toggleOverlay() {
  if (overlay.hasAttribute("hidden")) {
    openOverlay();
  } else {
    closeOverlay();
  }
}

function openOverlay() {
  if (!siteHeader.classList.contains(revealingClass)) {
    siteHeader.classList.remove(hiddenClass);
    siteHeader.classList.remove(hidingClass);
    siteHeader.classList.add(revealingClass);

    overlay.removeAttribute("hidden");
    scrollMenuLeftMask.removeAttribute("hidden");
    scrollMenuRightMask.removeAttribute("hidden");

    overlayTrigger.setAttribute("aria-expanded", true);
    overlayTrigger.setAttribute(
      "aria-label",
      overlayTrigger.getAttribute("aria-label").replace("Open", "Close")
    );

    document.body.classList.add(noScrollClass);
    document.addEventListener("keydown", onKeyPress, false);

    removeOverlayTriggerDot();
  }
}

function closeOverlay() {
  if (!siteHeader.classList.contains(hidingClass) && !overlay.hasAttribute("hidden")) {
    siteHeader.classList.remove(revealingClass);
    siteHeader.classList.add(hidingClass);

    overlayTrigger.setAttribute("aria-expanded", false);
    overlayTrigger.setAttribute(
      "aria-label",
      overlayTrigger.getAttribute("aria-label").replace("Close", "Open")
    );

    document.removeEventListener("keydown", onKeyPress);
  }
}

function onOverlayAnimationEnd() {
  if (siteHeader.classList.contains(revealingClass)) {
    siteHeader.classList.remove(revealingClass);
    overlayCloseButtons[0] && overlayCloseButtons[0].focus();
  } else if (siteHeader.classList.contains(hidingClass)) {
    siteHeader.classList.remove(hidingClass);
    siteHeader.classList.add(hiddenClass);

    overlay.setAttribute("hidden", "hidden");
    scrollMenuLeftMask.setAttribute("hidden", "hidden");
    scrollMenuRightMask.setAttribute("hidden", "hidden");

    overlayTrigger.focus();
    document.body.classList.remove(noScrollClass);
  }
}

function onKeyPress(e) {
  const code = e.code || e.keyCode;
  if (code === "Escape" || code === 27) {
    closeOverlay();
  }
}

function resizeScrollMenuMasks() {
  const scrollMenuContainerRect = scrollMenuContainer.getBoundingClientRect();
  const overlayTriggerRect = overlayTrigger.getBoundingClientRect();

  const beforeWidth = overlayTriggerRect.left - scrollMenuContainerRect.left;
  const afterWidth =
    scrollMenuContainerRect.left +
    scrollMenuContainerRect.width -
    (overlayTriggerRect.left + overlayTriggerRect.width);

  scrollMenuLeftMask.style.width = beforeWidth + "px";
  scrollMenuRightMask.style.width = afterWidth + "px";
}

function analyticsClick() {
  const payload = {
    event: "clickEvent",
    clickEvent: {
      feature: "header menu",
      type: "button",
      action: "click",
      label: "your globe"
    }
  };
  logger.info(`${debugTag} analytics: `, payload.clickEvent);
  window.analyticsLayer && window.analyticsLayer.push(payload);
}

function initOverlay() {
  logger.info(`${debugTag} initOverlay`);

  if (!siteHeader || !scrollMenuContainer || !scrollMenuList) {
    logger.info(`${debugTag} Elements not found`);
    return;
  }
  // Header
  siteHeader.classList.add("c-your-globe");
  siteHeader.classList.add("c-your-globe--hidden");

  // Overlay
  siteHeader.parentElement.insertAdjacentHTML(
    "beforeend",
    `<div id='c-your-globe-overlay' class='c-your-globe__overlay' hidden>
      <div class='c-your-globe__overlay-inner'>
        <div class='c-your-globe__overlay-content'>

          <div class='c-your-globe__overlay-header'>
            <div class='c-your-globe__overlay-header-text'></div>
            <button class='c-button c-button--icon-only c-button--white-primary c-button--no-border c-your-globe__overlay-close' type='button' aria-label='Close overlay'>
              <svg class='c-button__icon' role='img' pointer-events='none' focusable='false' aria-hidden='true'>
                <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
              </svg>
            </button>
          </div>

          <div class='c-your-globe__overlay-body'>
            <div class='c-spinner c-spinner-md c-spinner-light'></div>
          </div>

          <div class='c-your-globe__overlay-footer'>
            <button class='c-button c-button--icon-only c-button--white-primary c-button--no-border c-your-globe__overlay-close' type='button' aria-label='Close overlay'>
              <svg class='c-button__icon' role='img' pointer-events='none' focusable='false' aria-hidden='true'>
                <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>`
  );

  // Close buttons
  overlayCloseButtons = qsa(".c-your-globe__overlay .c-button");
  overlay = qs(".c-your-globe__overlay");
  overlay.addEventListener("animationend", () => {
    onOverlayAnimationEnd();
  });

  // Increment the analytics "position" values for all scroll menu items, since we're injecting a new item
  const scrollMenuLinks = qsa(".c-scroll-menu__link", scrollMenuList);
  scrollMenuLinks.forEach(function fn(link, index) {
    if (!link.dataset.analyticsclick) {
      return;
    }
    const analytics = JSON.parse(link.dataset.analyticsclick);
    analytics.position = index + 2;
    link.dataset.analyticsclick = JSON.stringify(analytics);
  });

  // Trigger
  scrollMenuList.insertAdjacentHTML(
    "afterbegin",
    `<li class="c-scroll-menu__item">
      <button type="button" class="c-scroll-menu__link c-your-globe__trigger"
        aria-label="Open Your Globe" aria-controls="c-your-globe-overlay" aria-expanded="false">
        Your Globe
      </button>
    </li>`
  );

  overlayTrigger = qs(".c-your-globe__trigger");
  overlayTrigger.addEventListener("click", function onTriggerClicked(e) {
    e.preventDefault();
    saveDateToLocalStorage();
    toggleOverlay(overlay);
    resizeScrollMenuMasks();
    analyticsClick();
  });

  overlay.addEventListener("click", function onOverlayClicked(e) {
    if (e.target.closest(".c-your-globe__overlay-close")) {
      closeOverlay();
    }
  });

  document.addEventListener(
    "click",
    function onBodyClicked(e) {
      if (!e.target.closest(".c-your-globe__overlay-content")) {
        closeOverlay();
      }
    },
    true
  );

  // Masks
  scrollMenuContainer.classList.add("c-your-globe__trigger-container");

  // Can't use ::before and ::after CSS pseudo-elements becuase we need to access the DOM nodes via JavaScript
  scrollMenuContainer.insertAdjacentHTML(
    "afterbegin",
    "<div class='c-your-globe__mask c-your-globe__mask--left' hidden></div>"
  );
  scrollMenuContainer.insertAdjacentHTML(
    "beforeend",
    "<div class='c-your-globe__mask c-your-globe__mask--right' hidden></div>"
  );

  scrollMenuLeftMask = document.querySelector(".c-your-globe__mask--left");
  scrollMenuRightMask = document.querySelector(".c-your-globe__mask--right");

  // Throttled event (see: https://developer.mozilla.org/en-US/docs/Web/API/Element/scroll_event)
  window.addEventListener("resize", function onResized() {
    if (!waiting) {
      window.requestAnimationFrame(function onRAF() {
        resizeScrollMenuMasks();
        waiting = false;
      });
      waiting = true;
    }
  });
}

/**
 * Saves current date to local storage (when overlay is opened)
 */
function saveDateToLocalStorage() {
  const now = new Date();
  const unixNow = Math.round(now.getTime() / 1000);
  local.set(keyLocalStorage, unixNow);
}

/**
 * Checks if there are new stories after the last time the user opened the overlay
 * @param {Array} stories
 * @returns {Boolean} - true if there is at least one new story
 */
function checkNewStories(stories) {
  const dateOverlayWasOpened = getDateOverlayWasOpened(keyLocalStorage);
  const datesUnix = stories.map(function convertDatesToUnix(story) {
    const dateAns = story.display_date || "";
    if (!dateAns) {
      return;
    }
    const dateFormatted = new Date(dateAns);
    const dateUnix = Math.round(dateFormatted.getTime() / 1000);
    return dateUnix;
  });
  const newStories = datesUnix.filter(function filterStories(storyDate) {
    return storyDate > dateOverlayWasOpened;
  });

  logger.info(
    `${debugTag} Date when the overlay/following page was last opened`,
    dateOverlayWasOpened
  );
  logger.info(`${debugTag} All unix dates`, datesUnix);
  logger.info(`${debugTag} Unix dates after filtering`, newStories);

  return newStories.length > 0;
}

/**
 * Get the unix date from local storage when the overlay was last opened
 * @param {String} key - to get
 * @returns {Number}
 */
function getDateOverlayWasOpened(key) {
  const dateUnix = local.get(key);
  return dateUnix;
}

export default function init() {
  // Your Globe overlay display criteria
  // - Must have `yourGlobeEnabled` runtime prop set to anyhting but `false`
  // - Must be a subscriber or registered user
  logger.info(`${debugTag} tgamConfig.env.yourGlobeEnabled`, tgamConfig.env.yourGlobeEnabled);
  logger.info(`${debugTag} isSubscribed`, isSubscribed());
  logger.info(`${debugTag} isRegistered`, isRegistered());
  const showYourGlobe =
    tgamConfig.env.yourGlobeEnabled !== "false" && (isSubscribed() || isRegistered());
  logger.info(`${debugTag} showYourGlobe: `, showYourGlobe);

  // Exclude IE users
  const ua = window.navigator.userAgent.toLowerCase();
  const isModernBrowser = ua.indexOf("msie") === -1 && ua.indexOf("trident") === -1;

  if (!isModernBrowser || !showYourGlobe) {
    return;
  }

  const base = getEnv();
  const hashId = getProp(tgamConfig, "datalayer.identity.guid", null);
  if (hashId) {
    initOverlay();
    getPerzonalizedTopics(hashId, base).then(function fn(data) {
      parsePerzonalizedTopicsData(data, hashId);
    });
  } else {
    logger.info(`${debugTag} Invalid hash ID`);
  }
}
